"use client"

import { type ReactNode, useEffect, useRef } from "react"
import Link from "next/link"
import cn from "classnames"

import buttonS from "~/components/primitives/Button/PrimaryButton.module.scss"
import { Icon } from "~/components/primitives/icon"
import { type ModalId, useModal } from "~/contexts/ModalContext"

import s from "./Modal.module.scss"

type ModalProps = {
  id: ModalId
  className?: string
  "aria-label"?: string
  children: ReactNode
}

// Only semantic first-party elements are made inert when the modal opens.
// Third-party overlays (hCaptcha challenge, Termly CMP, …) are <div>-based
// and intentionally excluded so they remain interactive.
const INERTABLE_TAGS = new Set([
  "HEADER",
  "MAIN",
  "FOOTER",
  "NAV",
  "ASIDE",
  "DIALOG",
])

export const Modal = ({ id, className, children, ...rest }: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const { isOpen, close } = useModal(id)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen && !dialog.open) {
      dialog.show()
      dialog.parentElement?.childNodes.forEach((node) => {
        if (
          node instanceof Element &&
          node !== dialog &&
          INERTABLE_TAGS.has(node.tagName) &&
          !node.hasAttribute("inert")
        ) {
          node.setAttribute("inert", "")
          node.setAttribute("data-dialog-inert", "")
        }
      })
    } else if (!isOpen && dialog.open) {
      dialog.close()
      document.querySelectorAll("[data-dialog-inert]").forEach((el) => {
        el.removeAttribute("inert")
        el.removeAttribute("data-dialog-inert")
      })
    }
  }, [isOpen])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      close()
    }

    dialog.addEventListener("close", close)
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      dialog.removeEventListener("close", close)
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [close])

  return (
    <dialog
      ref={dialogRef}
      aria-modal="true"
      className={cn(s.dialog, className)}
      {...rest}
    >
      <div className={s.wrapper}>
        <div className={s.header}>
          <div className={s.logoLink}>
            <Link href="/" aria-label="Meraki - Work with Soul Home">
              <Icon id="logo-effect" className={"z-logo-effect"} />
              <Icon id="logo" className={"z-logo"} />
            </Link>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close modal"
            className={cn(buttonS.primaryButton, s.closeButton)}
          >
            <span>Close</span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.707 0.707031L0.707031 16.707"
                stroke="currentColor"
                strokeWidth="2"
                strokeMiterlimit="10"
              />
              <path
                d="M0.707031 0.707031L16.707 16.707"
                stroke="currentColor"
                strokeWidth="2"
                strokeMiterlimit="10"
              />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </dialog>
  )
}
